import React, { useContext } from 'react';
import { Button as TpaButton, TextButton, HoverStyles, TextButtonPriority } from 'wix-ui-tpa/cssVars';
import { useSettings } from '@wix/tpa-settings/react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { withClientSideRenderMark } from '../../../../contexts/ClientSideRenderMark';
import { useShouldUseMobileSettings } from '../../../../hooks/useShouldUseMobileSettings';
import { CommonProps, RestrictedProps } from '../../../../types/common';
import settingsParams from '../../../Paywall/settingsParams';
import { st, classes } from './Restricted.st.css';

interface RestrictedViewConfiguration {
  title: string;
  button: string;
  link: string;
  onButtonClick(): void;
  onLinkClick(): void;
}

const RestrictedTextsContext = React.createContext<RestrictedViewConfiguration>({
  title: '',
  button: '',
  link: '',
  onButtonClick() {},
  onLinkClick() {},
});

const Restricted: React.FC<RestrictedProps & CommonProps> = ({
  missingPlan,
  navigateToHomePage,
  switchAccounts,
  navigateToPlanList,
}) => {
  const { t } = useTranslation();
  const { isMobile, isRTL, isEditor } = useEnvironment();
  const useMobileSettings = useShouldUseMobileSettings();

  const texts = React.useMemo<RestrictedViewConfiguration>(() => {
    return missingPlan || isEditor
      ? {
          title: t('restricted.title'),
          button: t('restricted.check-plans'),
          link: t('restricted.back'),
          onButtonClick: navigateToPlanList,
          onLinkClick: navigateToHomePage,
        }
      : {
          title: t('restricted.other-title'),
          button: t('restricted.back-to-site'),
          link: t('restricted.switch-account'),
          onButtonClick: navigateToHomePage,
          onLinkClick: switchAccounts,
        };
  }, [missingPlan, isEditor, navigateToPlanList, navigateToHomePage, switchAccounts, t]);

  return (
    <RestrictedTextsContext.Provider value={texts}>
      <div
        dir={isRTL ? 'rtl' : 'ltr'}
        data-hook="restricted-app"
        className={st(classes.wrapper, { mobile: isMobile, useMobileSettings })}
      >
        <div className={classes.container}>
          <Title />
          <div className={classes.buttonContainer}>
            <Button />
          </div>
          <div className={classes.linkContainer}>
            <Link />
          </div>
        </div>
      </div>
    </RestrictedTextsContext.Provider>
  );
};

const Title: React.FC = () => {
  const { title } = useContext(RestrictedTextsContext);
  const settings = useSettings();
  const htmlTag = settings.get(settingsParams.titleHtmlTag) || 'h1';
  const TitleTag = htmlTag as keyof JSX.IntrinsicElements;

  return (
    <div className={classes.titleContainer}>
      <TitleTag className={classes.title} data-hook="restricted-title">
        {title}
      </TitleTag>
    </div>
  );
};

const Button: React.FC = () => {
  const { button, onButtonClick } = useContext(RestrictedTextsContext);

  return (
    <TpaButton
      data-hook="restricted-button"
      onClick={() => onButtonClick()}
      hoverStyle={HoverStyles.underline}
      className={classes.tpaButton}
    >
      {button}
    </TpaButton>
  );
};

const Link: React.FC = () => {
  const { link, onLinkClick } = useContext(RestrictedTextsContext);

  return (
    <TextButton
      onClick={() => onLinkClick()}
      priority={TextButtonPriority.link}
      hoverStyle={HoverStyles.underline}
      className={classes.link}
      data-hook="restricted-link"
    >
      {link}
    </TextButton>
  );
};

export default withClientSideRenderMark(Restricted);
